import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useExtraFormToken,
  useSubmitExtraForm,
  type ExtraFormContractData,
} from "@/hooks/use-extra-form-token";
import ContractSignStep from "@/components/extra-form/ContractSignStep";
import logoFull from "@/assets/logos/solvrev-logo-full.png";

type PageState = "loading" | "form" | "sign" | "success" | "error";

// Full form state. WP3 (SEC-H1): personal_number/bank_account are never
// pre-filled from the server (no longer echoed) — they always start empty and
// are entered by the statist.
interface FormState {
  full_name: string;
  email: string;
  address: string;
  personal_number: string;
  bank_account: string;
  project_name: string;
}

export default function ExtraFormPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState<FormState>({
    full_name: "",
    email: "",
    address: "",
    personal_number: "",
    bank_account: "",
    project_name: "",
  });

  const tokenQuery = useExtraFormToken(token);
  const submitForm = useSubmitExtraForm();
  const submitting = submitForm.isPending;

  useEffect(() => {
    if (!token) {
      setErrorMsg("Ugyldig lenke");
      setState("error");
      return;
    }
    if (tokenQuery.isError) {
      setErrorMsg(tokenQuery.error.message);
      setState("error");
      return;
    }
    if (tokenQuery.isSuccess && tokenQuery.data) {
      // Only merge the non-sensitive pre-fill fields; PII stays empty (SEC-H1).
      const { full_name, email, address, project_name, contract } = tokenQuery.data;
      setForm((f) => ({ ...f, full_name, email, address, project_name }));
      // VIB-1282: purpose='contract_signing'-tokens har `contract` satt —
      // vis signeringssteget i stedet for det rene datainnsamlingsskjemaet.
      // Data er allerede komplett når en kontrakt er sendt (generate-extra-contract
      // nekter ufullstendige felt), så en kombinert form+sign-flyt er ikke nødvendig.
      setState(contract ? "sign" : "form");
    }
  }, [token, tokenQuery.isError, tokenQuery.isSuccess, tokenQuery.data, tokenQuery.error]);

  const contract: ExtraFormContractData | undefined = tokenQuery.data?.contract;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    submitForm.mutate(
      {
        token,
        full_name: form.full_name,
        address: form.address,
        personal_number: form.personal_number,
        bank_account: form.bank_account,
      },
      {
        onSuccess: () => setState("success"),
        onError: (err) => setErrorMsg(err.message),
      },
    );
  };

  return (
    <div className="min-h-screen bg-[var(--sr-revegraa)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand-header: hvit logo på mørk stripe (Sølvrev brandguide) */}
        <div className="rounded-2xl bg-[var(--sr-revemoerk)] px-6 py-5 mb-5 text-center">
          <img src={logoFull} alt="Sølvrev" className="h-7 w-auto mx-auto" />
          <p className="text-xs text-white/70 mt-2 tracking-wide">Registrering av ekstern medarbeider</p>
        </div>

        {state === "loading" && (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--sr-reveblaa)] mx-auto" />
            <p className="text-sm text-gray-500 mt-4">Laster skjema...</p>
          </div>
        )}

        {state === "error" && (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium mb-2">Beklager</p>
            <p className="text-sm text-gray-500">{errorMsg}</p>
          </div>
        )}

        {state === "success" && (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium mb-2">Takk!</p>
            <p className="text-sm text-gray-500">
              {contract
                ? "Kontrakten er signert. Du kan lukke denne siden."
                : "Informasjonen din er lagret. Du kan lukke denne siden."}
            </p>
          </div>
        )}

        {state === "sign" && contract && token && (
          <ContractSignStep
            token={token}
            contract={contract}
            projectName={form.project_name}
            onSigned={() => setState("success")}
          />
        )}

        {state === "form" && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
            {form.project_name && (
              <p className="text-sm text-gray-500 text-center pb-2 border-b">
                Prosjekt: <span className="font-medium text-gray-700">{form.project_name}</span>
              </p>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fullt navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)] focus:border-transparent"
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <input
                type="text"
                className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)] focus:border-transparent"
                placeholder="Gateadresse, postnummer, sted"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personnummer (11 siffer) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                inputMode="numeric"
                pattern="\d{11}"
                maxLength={11}
                className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)] focus:border-transparent"
                placeholder="01019012345"
                value={form.personal_number}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setForm((f) => ({ ...f, personal_number: v }));
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kontonummer (11 siffer) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                inputMode="numeric"
                pattern="\d{11}"
                maxLength={11}
                className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)] focus:border-transparent"
                placeholder="12345678901"
                value={form.bank_account}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setForm((f) => ({ ...f, bank_account: v }));
                }}
              />
            </div>

            {errorMsg && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{errorMsg}</p>
            )}

            {/* GDPR notice */}
            <div className="bg-gray-50 rounded-lg px-3 py-2.5 text-xs text-gray-500 leading-relaxed">
              Informasjonen brukes kun til utbetaling av honorar for dette prosjektet.
              Data behandles av Sølvrev AS og slettes etter at utbetaling er gjennomført
              og prosjektet er avsluttet. Du kan når som helst be om innsyn eller sletting ved å
              kontakte oss.
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[var(--sr-reveblaa)] hover:opacity-90 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-opacity"
            >
              {submitting ? "Sender..." : "Send inn"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
